import {
  BanIcon,
  CheckCircle2Icon,
  ClockIcon,
  MessagesSquareIcon,
  PauseCircleIcon,
  PhoneOffIcon,
  ThumbsDownIcon,
  UserRoundXIcon,
  VolumeXIcon,
} from "lucide-react";

import { Row, Text } from "@/components";

import { SallyState } from "@/types";

// Where a candidate ended up with Sally on this job. The four "owed" states are
// the ones a human should take a second run at — candidates often answer a
// person after ignoring the agent, and vice versa. OPTED_OUT is styled as a stop
// sign rather than an opportunity: it exists so nobody texts them by hand.
const STATE_CONFIG: Record<
  SallyState,
  { className: string; Icon: typeof BanIcon; label: string }
> = {
  [SallyState.PENDING_OUTREACH]: {
    className: "border-slate-200 bg-slate-50 text-slate-600",
    Icon: ClockIcon,
    label: "Queued for Sally",
  },
  [SallyState.IN_CONVERSATION]: {
    className: "border-blue-200 bg-blue-50 text-blue-700",
    Icon: MessagesSquareIcon,
    label: "Talking to Sally",
  },
  [SallyState.CONVERTED]: {
    className: "border-green-200 bg-green-50 text-green-700",
    Icon: CheckCircle2Icon,
    label: "Converted by Sally",
  },
  [SallyState.RULE_BLOCKED]: {
    className: "border-violet-200 bg-violet-50 text-violet-700",
    Icon: PauseCircleIcon,
    label: "Sally held off",
  },
  [SallyState.UNREACHABLE]: {
    className: "border-amber-200 bg-amber-50 text-amber-700",
    Icon: VolumeXIcon,
    label: "Sally got no reply",
  },
  [SallyState.DECLINED]: {
    className: "border-slate-200 bg-slate-50 text-slate-700",
    Icon: ThumbsDownIcon,
    label: "Declined Sally",
  },
  [SallyState.BAD_CONTACT]: {
    className: "border-orange-200 bg-orange-50 text-orange-700",
    Icon: PhoneOffIcon,
    label: "Bad contact info",
  },
  [SallyState.OPTED_OUT]: {
    className: "border-red-200 bg-red-50 text-red-700",
    Icon: BanIcon,
    label: "Opted out — do not contact",
  },
  [SallyState.STAFFER_REJECTED]: {
    className: "border-slate-200 bg-slate-50 text-slate-500",
    Icon: UserRoundXIcon,
    label: "Passed by a staffer",
  },
  [SallyState.JOB_CLOSED]: {
    className: "border-slate-200 bg-slate-50 text-slate-500",
    Icon: BanIcon,
    label: "Job closed mid-outreach",
  },
};

export const SearchV2SallyStateBadge = ({
  reason,
  state,
}: {
  reason: string | null;
  state: SallyState;
}) => {
  /* Render */
  // A state added in guac but not mapped here would otherwise blow up on an
  // undefined lookup; rendering nothing is the safer failure.
  const config = STATE_CONFIG[state] as
    | (typeof STATE_CONFIG)[SallyState]
    | undefined;
  if (!config) {
    return null;
  }
  const { className, Icon, label } = config;

  // Sized to match the card's other pills (see renderInterviewBadge): h-5,
  // text-xs, leading-none — so neighboring badges sit on one visual line.
  return (
    <Row
      className={`h-5 shrink-0 items-center gap-1.5 rounded-full border px-2 ${className}`}
    >
      <Icon className="h-3 w-3" />
      <Text className="text-xs font-medium leading-none text-inherit">
        {reason ? `${label} · ${reason}` : label}
      </Text>
    </Row>
  );
};
